import { Star, Mail, Phone, Linkedin, FileText, GraduationCap } from 'lucide-react';
import type { Candidate } from '@core/types';
import { CandidateResumePreviewButton } from './CandidateResumePreviewButton';

/**
 * Renders as a `ggj-profile-section-body` block inside the Profile tab's
 * outer `ggj-param-group` container with no gap or card of its own.
 */
export function CandidateProfileSummary({ candidate }: { candidate: Candidate }) {
  const rating = Math.min(5, Math.max(0, candidate.recruiter_rating ?? 0));
  const li = candidate.linkedin_url?.trim();
  const linkedinHref = li ? (li.startsWith('http') ? li : `https://${li}`) : null;
  const education = candidate.education ?? [];

  return (
    <>
      <div className="ggj-profile-section-body">
        <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary">Recruiter rating</p>
              <div className="flex items-center gap-0.5" aria-label={`Rating ${rating} of 5`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`h-5 w-5 ${rating >= n ? 'fill-amber-400 text-amber-400' : 'text-[var(--vscode-border-primary)]'}`}
                  />
                ))}
                <span className="ml-2 text-sm vscode-text-secondary tabular-nums">{rating}/5</span>
              </div>
            </div>
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary">Social &amp; contact</p>
              <div className="flex flex-wrap gap-1.5">
                {candidate.email ? (
                  <a
                    href={`mailto:${encodeURIComponent(candidate.email)}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[var(--vscode-border-primary)] px-2 py-1 text-xs font-medium vscode-text-primary transition-colors hover:bg-[var(--vscode-bg-hover)]"
                  >
                    <Mail className="h-3.5 w-3.5 shrink-0 opacity-80" />
                    Email
                  </a>
                ) : null}
                {candidate.phone ? (
                  <a
                    href={`tel:${candidate.phone.replace(/\s/g, '')}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[var(--vscode-border-primary)] px-2 py-1 text-xs font-medium vscode-text-primary transition-colors hover:bg-[var(--vscode-bg-hover)]"
                  >
                    <Phone className="h-3.5 w-3.5 shrink-0 opacity-80" />
                    Call
                  </a>
                ) : null}
                {linkedinHref ? (
                  <a
                    href={linkedinHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-[var(--vscode-border-primary)] px-2 py-1 text-xs font-medium vscode-text-primary transition-colors hover:bg-[var(--vscode-bg-hover)]"
                  >
                    <Linkedin className="h-3.5 w-3.5 shrink-0" />
                    LinkedIn
                  </a>
                ) : null}
              </div>
            </div>
          </div>

          <div className="min-w-0 flex-1 space-y-3 lg:border-l lg:border-[var(--vscode-border-primary)] lg:pl-5">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-0.5 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> Background / notes
              </p>
              <p className="text-sm vscode-text-primary leading-snug whitespace-pre-wrap">
                {candidate.notes?.trim() ? candidate.notes : <span className="vscode-text-tertiary">No background notes yet.</span>}
              </p>
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-0.5 flex items-center gap-1.5">
                <GraduationCap className="w-3.5 h-3.5" /> Education
              </p>
              {education.length === 0 ? (
                <p className="text-sm vscode-text-tertiary">No education entries yet.</p>
              ) : (
                <ul className="space-y-1">
                  {education.map((e, i) => (
                    <li key={i} className="text-sm border border-[var(--vscode-border-primary)] rounded-md px-2 py-1 bg-[var(--vscode-bg-secondary)]">
                      <span className="font-medium vscode-text-primary">{e.degree || '—'}</span>
                      <span className="vscode-text-secondary"> · {e.institution || '—'}</span>
                      {e.year != null ? <span className="vscode-text-tertiary text-xs"> ({e.year})</span> : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-0.5">Résumé</p>
              {candidate.base_resume_url ? (
                <CandidateResumePreviewButton
                  storagePath={candidate.base_resume_url}
                  fullName={candidate.full_name}
                  currentTitle={candidate.current_title}
                  eyebrow="Candidate résumé"
                  label="View candidate résumé"
                />
              ) : (
                <p className="text-sm vscode-text-tertiary">No résumé on file.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
